import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type AuthMeResponse } from "@/lib/api/client";
import { readSignupSource } from "@/lib/onboarding/site-prefill";
import {
  useAuthSession,
  useAuthUser,
} from "@/components/providers/auth-context";

export interface UseProvisioningResult {
  isProvisioned: boolean;
  isProvisioning: boolean;
  provisioningError: Error | null;
  profile: AuthMeResponse | null;
  refetch: () => void;
}

/**
 * Hook to ensure user is provisioned in the backend
 *
 * Calls /auth/me after authentication to:
 * 1. Provision tenant/user if this is a new sign-up
 * 2. Retrieve user profile and tenant info
 *
 * Passes email/name from Descope SDK since these may not be in the JWT.
 *
 * Other data-fetching hooks should depend on this being complete
 * before making requests.
 */
export function useProvisioning(): UseProvisioningResult {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { user, isUserLoading } = useAuthUser();
  const queryClient = useQueryClient();

  const enabled = isAuthenticated && !isSessionLoading && !isUserLoading;

  const { data, isPending, isFetching, error, refetch, isSuccess } = useQuery({
    queryKey: ["auth", "me", user?.email],
    queryFn: () => {
      // Where the signup came from (task 299). Sent on every call rather than
      // only the first, because the SPA cannot tell which call will provision
      // — the server decides that, and ignores this on every other one. It is
      // read from `sessionStorage`, which `main.tsx` writes before React
      // renders, so a Descope social-login redirect does not lose it.
      const signupSource = readSignupSource();
      return api.auth.me({
        email: user?.email,
        name: user?.name,
        ...(signupSource ? { signupSource } : {}),
      });
    },
    // Wait for both session and user data to be available
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry a refusal. 401 (not authenticated) and 400 (bad request)
      // were always here; 403 is the provisioning gates — an unverified
      // address, or a member whose local record went missing (task 199) — and
      // every one of them is a deterministic "no" that three more identical
      // requests cannot turn into a yes.
      //
      // Retrying them is not merely wasted: `/auth/me` runs on every load, and
      // the per-IP provisioning rate limit is charged before the gates are
      // reached, so a user stuck behind one was spending four times the budget
      // per load and could 429 genuine signups sharing their NAT.
      if (error instanceof Error && "status" in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 400 || status === 403) {
          return false;
        }
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
  });

  // isChecking is true when:
  // 1. User data is still loading (isUserLoading)
  // 2. Query is actively fetching (isFetching)
  // 3. Query is enabled but hasn't completed yet (enabled && isPending)
  // This distinguishes "checking status" from "not yet ready to check"
  const isChecking = isUserLoading || isFetching || (enabled && isPending);

  return {
    isProvisioned: isSuccess && !!data,
    isProvisioning: isChecking,
    provisioningError: error as Error | null,
    profile: data ?? null,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      refetch();
    },
  };
}
